// Assistant launcher (Plan 22).
//
// Only the floating button ships in the main bundle; the drawer — which pulls
// in the AI SDK and a markdown renderer — is code-split and fetched the first
// time someone opens it. Keeping the two in one module cost ~290 kB on every
// dashboard load for a feature most sessions never open.

import { Sparkles } from "lucide-react";
import { Suspense, useState } from "react";
import { lazyRetry } from "../../lib/lazy-retry";
import { TENANT_PROFILE } from "../../lib/tenant";
import { FU, T, W } from "./tokens";

const AssistantDrawer = lazyRetry(() => import("./assistant-drawer").then((m) => ({ default: m.AssistantDrawer })));

export type { AssistantContext } from "./assistant-drawer";
import type { AssistantContext } from "./assistant-drawer";

export function AssistantPanel({
  context,
  variant = "default",
}: {
  context?: AssistantContext;
  /** "dark" — for the near-black landing hub, where the navy pill disappears. */
  variant?: "default" | "dark";
}) {
  const [open, setOpen] = useState(false);
  // Once opened, the drawer stays mounted for the rest of the page session so
  // the conversation survives closing and reopening it. Unmounting reset
  // useChat's state, which threw away the thread every time the panel closed.
  const [mounted, setMounted] = useState(false);

  if (!TENANT_PROFILE.assistantEnabled) return null;

  if (open || mounted) {
    // No fallback UI: the chunk resolves in a few hundred ms and a flashing
    // placeholder drawer reads worse than the button staying put a beat longer.
    return (
      <Suspense fallback={null}>
        <AssistantDrawer open={open} context={context} onClose={() => setOpen(false)} />
        {!open && launcher()}
      </Suspense>
    );
  }

  return launcher();

  function launcher() {
    return (
      <>
        <button
          type="button"
          onClick={() => {
            setMounted(true);
            setOpen(true);
          }}
          aria-label="Ask assistant - open the Assistant Chat Bot"
          className="asst-launcher"
          data-variant={variant}
          style={{
            position: "fixed",
            right: 24,
            // Symmetric inset — the bottom gap matches the right gap.
            bottom: 24,
            zIndex: 90,
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 18px",
            borderRadius: 999,
            fontFamily: FU,
            fontSize: T.body,
            fontWeight: W.medium,
            cursor: "pointer",
          }}
        >
          <Sparkles size={16} />
          Ask assistant
        </button>
        {/* Colour/border/shadow live in CSS (not inline) so the light-mode rule
            below can lighten the pill; dark + the landing-hub variant keep their
            exact values. */}
        <style>{`
          .asst-launcher {
            border: none;
            background: var(--color-fill-strong);
            color: var(--color-on-fill-strong);
            box-shadow: 0 6px 24px rgba(0, 0, 0, 0.22);
          }
          /* Landing-hub variant — glass pill on the near-black hub, theme-agnostic. */
          .asst-launcher[data-variant="dark"] {
            border: 1px solid rgba(255, 255, 255, 0.16);
            background: rgba(255, 255, 255, 0.08);
            color: #e8ecf7;
            backdrop-filter: blur(6px);
          }
          /* Light mode lightens BOTH launcher pills — the in-app default AND the
             landing-hub ("dark") variant. The hub is near-black in dark mode, so
             its pill is a light glass chip; but in light mode the hub is a pale
             page and that white-on-white glass chip vanished — the reported bug.
             In light mode both become a clean white pill with navy ink (the tonal
             mirror of the dark-on-dark launcher) and the blur is cancelled. Dark
             mode is untouched, so the glass chip on the near-black hub stays
             byte-identical. */
          [data-theme="light"] .asst-launcher[data-variant="default"],
          [data-theme="light"] .asst-launcher[data-variant="dark"] {
            background: #ffffff;
            color: #0e1a3a;
            border: 1px solid rgba(0, 15, 71, 0.12);
            box-shadow: 0 6px 20px rgba(0, 15, 71, 0.14);
            backdrop-filter: none;
          }
        `}</style>
      </>
    );
  }
}
